import React, { useContext, useState } from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { StandAloneLink } from '@hz-design-system/web-ui';

import FavouriteButton from '../../../ListingApp/components/Listing/FavouriteButton';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';

import Classes from './ListingHeader.scss';
import { TListingClient } from 'types/TListing';
import getAdUrl from '../../../SocialShareApp/utils/getAdUrl';
import isMobile from '../../../../utils/isMobile';
import formatPrice from '../../../../utils/formatPrice';
import { priceTypes } from '../../../../../../server/utils/constants';
import ShippingInformation from '../../../ListingApp/components/Listing/ShippingInformation';
import BuyersProtectionModalLink from '../../../ListingApp/components/Listing/BuyersProtection/ModalLink/ModalLink';
import useExperimentPopOver from './hooks/useExperimentPopover';
import { trackGAEvent } from '../../../../utils/gaTracking';

type TListingHeaderProps = {
  listing: TListingClient;
};

let SocialShare;
let BasicModal;

const ListingHeader = ({ listing }: TListingHeaderProps) => {
  const { t } = useI18nContext();
  const {
    userDetails: { isAdOwner = false } = {},
    shippingLabelsEnabled,
    buyerProtectionEnabled,
  } = useContext(EnvironmentContext);
  const {
    itemId,
    title,
    priceInfo,
    flags: { isAdmarkt, isLeaseCar, showLoanIndicators, shippable },
    isSavingEnabled,
    isSaved,
    isCarAd,
    shippingInformation,
    buyersProtectionAllowed,
    buyersProtectionModalImageUrl,
    stats,
  } = listing;
  const isCurrency = [priceTypes.MIN_BID, priceTypes.FIXED].includes(priceInfo.priceType);
  const isPricePerMonth = isLeaseCar && isCurrency;
  const [isSocialShareModalVisible, setIsSocialShareModalVisible] = useState(false);
  const { ExperimentPopOver } = useExperimentPopOver();

  const handleShareToggle = async (e) => {
    e.preventDefault();
    const shareData = { title, url: getAdUrl() };
    const navigatorShareIsAvailable = navigator.canShare && navigator.canShare(shareData) && isMobile();

    if (navigatorShareIsAvailable) {
      trackGAEvent('ShareAdBegin', 'navigator');
      navigator.share(shareData);
    } else {
      SocialShare = (
        await import(/* webpackChunkName: "socialShare" */ '../../../SocialShareApp/components/SocialShare/SocialShare')
      ).default;
      BasicModal = (await import(/* webpackChunkName: "BasicModal" */ '@hz-design-system/web-ui')).BasicModal;
      setIsSocialShareModalVisible(true);
    }
  };

  return (
    <>
      <div className={Classes.actionsContainer}>
        <div className={Classes.actions}>
          {isSavingEnabled && !isAdOwner && (
            <div className={Classes.action}>
              <FavouriteButton
                asLink
                isSaved={isSaved}
                itemId={itemId}
                favoritedCount={stats.favoritedCount}
                isDisplayedOnMobile
              />
            </div>
          )}
          <div className={Classes.action}>
            <StandAloneLink onClick={handleShareToggle} aria-label={t('share.with')} iconLeft="share_web">
              <Trans tagName="span" i18nKey={'share.label'} />
            </StandAloneLink>
          </div>
        </div>

        {Boolean(ExperimentPopOver) && <ExperimentPopOver />}
      </div>

      <header className={Classes.header}>
        <h1 className={Classes.title}>{title}</h1>
        {isPricePerMonth && (
          <div className={Classes.price}>
            <span className={Classes.pricePerMonth}>{t('listing.leaseprice.prefix')}</span>
            <span>{` ${formatPrice(listing.priceInfo, t, isCarAd)} `}</span>
            <span className={Classes.pricePerMonth}>{` ${t('listing.leaseprice.suffix')}`}</span>
            {showLoanIndicators && <span>*</span>}
          </div>
        )}
        {!isPricePerMonth && <div className={Classes.price}>{formatPrice(listing.priceInfo, t, isCarAd)}</div>}
        {!isCarAd && (
          <ShippingInformation
            shippingInformation={shippingInformation}
            shippable={shippable}
            shippingLabelsEnabled={shippingLabelsEnabled}
            isAdmarkt={isAdmarkt}
          />
        )}
        {buyersProtectionModalImageUrl && buyersProtectionAllowed && buyerProtectionEnabled && !isCarAd && (
          <BuyersProtectionModalLink imageUrl={buyersProtectionModalImageUrl} />
        )}
      </header>
      {isSocialShareModalVisible && (
        <BasicModal title={t('share.label')} onClose={() => setIsSocialShareModalVisible(false)}>
          <SocialShare listing={listing} asModal />
        </BasicModal>
      )}
    </>
  );
};

export default ListingHeader;
